import { lazy } from 'react';
import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { useMe } from './api/hooks';
import { Anmeldung } from './auth/Anmeldung';
import { Fehleranzeige } from './components/Fehleranzeige';
import { Ladeanzeige } from './components/Ladeanzeige';
import { Layout } from './components/Layout';
import { RollenGuard } from './components/RollenGuard';
import { KeinTeilnehmerSeite } from './pages/KeinTeilnehmerSeite';
import { NichtGefundenSeite } from './pages/NichtGefundenSeite';

// Code-Splitting: Jede Seite ist ein eigener Chunk, der erst beim Aufruf geladen wird.
const MeineSchulungenSeite = lazy(() =>
  import('./pages/MeineSchulungenSeite').then((m) => ({ default: m.MeineSchulungenSeite })),
);
const ModulTermineSeite = lazy(() =>
  import('./pages/ModulTermineSeite').then((m) => ({ default: m.ModulTermineSeite })),
);
const MeineBuchungenSeite = lazy(() =>
  import('./pages/MeineBuchungenSeite').then((m) => ({ default: m.MeineBuchungenSeite })),
);
const AdminSeite = lazy(() =>
  import('./pages/AdminSeite').then((m) => ({ default: m.AdminSeite })),
);

export function App() {
  return (
    <Anmeldung>
      <Routen />
    </Anmeldung>
  );
}

function Routen() {
  const me = useMe();

  if (me.isPending) return <Ladeanzeige text="Anmeldung wird geprüft …" />;
  if (me.isError)
    return <Fehleranzeige fehler={me.error} erneutVersuchen={() => void me.refetch()} />;

  const benutzer = me.data;
  const startseite = benutzer.istTeilnehmer
    ? '/schulungen'
    : benutzer.rollen.includes('Admin')
      ? '/admin'
      : '/hinweis';
  const nurTeilnehmer = (seite: ReactNode) =>
    benutzer.istTeilnehmer ? seite : <Navigate to={startseite} replace />;

  return (
    <Routes>
      <Route element={<Layout benutzer={benutzer} />}>
        <Route index element={<Navigate to={startseite} replace />} />
        <Route
          path="hinweis"
          element={<KeinTeilnehmerSeite ansprechpartner={benutzer.ansprechpartner} />}
        />
        <Route path="schulungen" element={nurTeilnehmer(<MeineSchulungenSeite />)} />
        <Route path="schulungen/modul/:modulId" element={nurTeilnehmer(<ModulTermineSeite />)} />
        <Route path="buchungen" element={nurTeilnehmer(<MeineBuchungenSeite />)} />
        <Route
          path="admin/*"
          element={
            <RollenGuard benutzer={benutzer} erlaubt={['Admin']}>
              <AdminSeite />
            </RollenGuard>
          }
        />
        <Route path="*" element={<NichtGefundenSeite />} />
      </Route>
    </Routes>
  );
}
